import { useMemo } from 'react'
import { addDays, format, startOfDay } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { computeSlots, DEFAULT_SLOT_PARAMS } from '@/lib/scheduling'
import type { Availability } from '@/types/availability'

interface EffectiveSchedulePreviewProps {
  availability: Availability
  timezone: string
}

const PREVIEW_WEEKS = 4

export function EffectiveSchedulePreview({ availability, timezone }: EffectiveSchedulePreviewProps) {
  const slotsByDate = useMemo(() => {
    if (!timezone) return new Map<string, number>()
    const now = new Date()
    const from = startOfDay(now)
    const to = addDays(from, PREVIEW_WEEKS * 7)
    const slots = computeSlots({
      availability,
      providerTimezone: timezone,
      from,
      to,
      now,
      // Preview shows the provider's configured schedule, not what a patient
      // could book right now — so ignore the booking lead cutoff.
      params: { ...DEFAULT_SLOT_PARAMS, min_lead_minutes: 0 },
    })
    const byDate = new Map<string, number>()
    for (const slot of slots) {
      const zoned = toZonedTime(new Date(slot.start_at), timezone)
      const key = format(zoned, 'yyyy-MM-dd')
      byDate.set(key, (byDate.get(key) ?? 0) + 1)
    }
    return byDate
  }, [availability, timezone])

  const days = useMemo(() => {
    const from = startOfDay(new Date())
    return Array.from({ length: PREVIEW_WEEKS * 7 }, (_, i) => addDays(from, i))
  }, [])

  if (!timezone) {
    return <p className="text-sm text-slate-500">Select a timezone to see the preview.</p>
  }

  const totalSlots = Array.from(slotsByDate.values()).reduce((a, b) => a + b, 0)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-900">
          Next {PREVIEW_WEEKS} weeks
        </h3>
        <span className="text-xs text-slate-500">
          {totalSlots} slot{totalSlots === 1 ? '' : 's'} available
        </span>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map((d) => {
          const key = format(d, 'yyyy-MM-dd')
          const count = slotsByDate.get(key) ?? 0
          return (
            <div
              key={key}
              className={`rounded-md border p-2 text-center ${
                count > 0 ? 'border-primary-200 bg-primary-50' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="text-xs font-medium text-slate-600">{format(d, 'EEE')}</div>
              <div className="text-xs text-slate-500">{format(d, 'MMM d')}</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{count}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
